import { useState } from "react";
import { ShieldCheck, Smartphone } from "lucide-react";
import { useUserMFA } from "@/hooks/profile/useUserMFA";

interface UserMFAVerifyProps {
  onVerified: () => void;
}

export function UserMFAVerify({ onVerified }: UserMFAVerifyProps) {
  const { verifyMFA, isLoading } = useUserMFA();
  const [verificationCode, setVerificationCode] = useState("");

  const handleVerify = async () => {
    if (!verificationCode.trim()) return;

    const result = await verifyMFA(verificationCode);
    if (result.success) {
      onVerified();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && verificationCode.length === 6) {
      handleVerify();
    }
  };

  return (
    <div className="light min-h-screen flex items-center justify-center p-6 bg-[#F7F7FB] font-archivo">
      <div className="w-full max-w-[448px] rounded-[3px] border border-[#1E1B4B]/[0.12] bg-white p-7 flex flex-col gap-[18px]">
        <span className="self-center h-12 w-12 rounded-full bg-[#5A31D8]/10 flex items-center justify-center">
          <ShieldCheck size={24} className="text-[#5A31D8]" strokeWidth={1.7} />
        </span>
        <div className="flex flex-col gap-2 text-center">
          <h2 className="text-[21px] font-bold leading-tight text-[#1E1B4B]">
            Two-factor verification required
          </h2>
          <p className="text-sm leading-relaxed text-[#6E6B8A]">
            Enter the 6-digit code from your authenticator app to continue.
          </p>
        </div>

        <div className="flex flex-col gap-[7px]">
          <label htmlFor="code" className="text-[13.5px] font-medium text-[#1E1B4B]">
            Verification code
          </label>
          <input
            id="code"
            placeholder="Enter 6-digit code"
            value={verificationCode}
            onChange={(e) =>
              setVerificationCode(e.target.value.replace(/\D/g, ""))
            }
            onKeyDown={handleKeyDown}
            maxLength={6}
            autoFocus
            className="h-11 px-3 rounded-[3px] border border-[#1E1B4B]/[0.18] text-center font-jetbrains text-lg tracking-[.2em] text-[#1E1B4B] placeholder:text-[#8B88A3] placeholder:tracking-normal placeholder:font-archivo placeholder:text-sm bg-white focus:outline-none focus:border-[#5A31D8]"
          />
        </div>

        <button
          type="button"
          onClick={handleVerify}
          disabled={isLoading || verificationCode.length !== 6}
          className="h-[42px] inline-flex items-center justify-center gap-2 rounded-[3px] bg-[#5A31D8] hover:bg-[#4526A8] font-semibold text-sm text-white disabled:bg-[#8B88A3] disabled:hover:bg-[#8B88A3]"
        >
          {isLoading ? (
            "Verifying..."
          ) : (
            <>
              <Smartphone size={15} strokeWidth={1.8} />
              Verify
            </>
          )}
        </button>

        <p className="text-center text-[13.5px] leading-relaxed text-[#6E6B8A]">
          Lost access to your app?{" "}
          <a
            href="https://www.paystreem.com/support"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[#5A31D8] hover:text-[#3F1FA8]"
          >
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}
